import { Link, LinkTarget, LinkVariant } from '@ampcore/adel-react'
import React from 'react'

import { DisplayGrid } from '../DisplayGrid/DisplayGrid'
import { getBackgroundConfig } from './getBackgroundConfig'
import {
  getButtonClassNames,
  renderCertificateRates,
  renderEyebrowText,
  renderIconEyebrowImage,
  renderInnerContent,
  TwoColumnBannerProps
} from './TwoColumnBannerColumns'
import { TwoColumnBannerShell } from './TwoColumnBannerShell'

// Aligned layout: the two columns are placed on a shared row grid so each
// content block (eyebrow, body, buttons) lines up across the columns. Used for
// text-only banners. DisplayGrid depth=1 extracts the inner block <div>s of
// each column onto the shared grid, so the column divs stay containers.
export const TwoColumnBannerAligned = (props: TwoColumnBannerProps) => {
  const {
    psnAnalytics,
    textColor,
    leftIcon,
    leftIconColor,
    fillLeft,
    strokeLeft,
    leftEyebrowImage,
    leftEyebrowImageAlt,
    leftEyebrowText,
    leftPlaylistId,
    leftColumnContent,
    leftCertificateRates,
    leftPrimaryBtnText,
    leftPrimaryBtnUrl,
    leftPrimaryBtnTarget,
    leftPrimaryBtnAnalytics,
    leftTertiaryBtnText,
    leftTertiaryBtnUrl,
    leftTertiaryBtnTarget,
    leftTertiaryBtnAnalytics,
    leftBackgroundColor,
    rightIcon,
    rightIconColor,
    fillRight,
    strokeRight,
    rightEyebrowImage,
    rightEyebrowText,
    rightEyebrowImageAlt,
    rightPlaylistId,
    rightColumnContent,
    rightCertificateRates,
    rightPrimaryBtnText,
    rightPrimaryBtnUrl,
    rightPrimaryBtnTarget,
    rightPrimaryBtnAnalytics,
    rightTertiaryBtnText,
    rightTertiaryBtnUrl,
    rightTertiaryBtnTarget,
    rightTertiaryBtnAnalytics,
    rightBackgroundColor,
    btnColor,
    testId
  } = props

  const hasLeftColumnImage = leftColumnContent?.includes('<img')
  const hasRightColumnImage = rightColumnContent?.includes('<img')
  const hasAnyImage = hasLeftColumnImage || hasRightColumnImage
  const hasBothImages = hasLeftColumnImage && hasRightColumnImage
  const hasLeftColumnTable = leftColumnContent?.includes('<table')
  const hasRightColumnTable = rightColumnContent?.includes('<table')

  const { primaryBtnClassName, tertiaryBtnClassName } =
    getButtonClassNames(btnColor)

  const leftColumn = (
    <div
      className={`TwoColumnBanner-columnLeft ${leftBackgroundColor ? getBackgroundConfig(leftBackgroundColor).classes : ''} ${
        leftCertificateRates ? `u-borderRadius24 u-padding24` : ''
      }`}
    >
      {leftCertificateRates ? (
        renderCertificateRates('left', leftCertificateRates)
      ) : (
        <>
          {renderIconEyebrowImage({
            side: 'left',
            icon: leftIcon,
            iconColor: leftIconColor,
            fill: fillLeft,
            stroke: strokeLeft,
            eyebrowImage: leftEyebrowImage,
            eyebrowImageAlt: leftEyebrowImageAlt,
            containerClassName:
              'u-sm-paddingTop4 u-md-paddingTop8 u-paddingVert0',
            testId
          })}
          <div>
            {renderEyebrowText({
              side: 'left',
              eyebrowText: leftEyebrowText,
              className: `TwoColumnBanner-eyebrowText Type-5 u-textEyebrow u-paddingVert8 u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40`,
              testId
            })}
          </div>
          <div>
            {renderInnerContent({
              side: 'left',
              containerClassName: [
                'TwoColumnBanner-innerContent u-paddingBottom8',
                !hasLeftColumnImage &&
                  'u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40'
              ]
                .filter(Boolean)
                .join(' '),
              columnContent: leftColumnContent,
              hasColumnTable: hasLeftColumnTable,
              backgroundColor: leftBackgroundColor,
              textColor,
              descriptionPlaylistId: rightPlaylistId,
              descriptionContainerClassName:
                'TwoColumnBanner-videoDescriptionContainer',
              playerPlaylistId: leftPlaylistId,
              testId
            })}
          </div>
          <div>
            {(leftPrimaryBtnUrl || leftTertiaryBtnUrl) && (
              <div
                className={`u-sm-paddingBottom4 u-md-paddingBottom8 u-paddingBottom0 u-paddingTop32 ${
                  !hasAnyImage
                    ? 'u-sm-paddingBottom40 u-md-paddingBottom40'
                    : ''
                }`}
              >
                {leftPrimaryBtnUrl && (
                  <Link
                    variant={LinkVariant.PRIMARY}
                    name={leftPrimaryBtnText ?? ''}
                    url={leftPrimaryBtnUrl}
                    target={leftPrimaryBtnTarget as LinkTarget}
                    analyticsName={leftPrimaryBtnAnalytics}
                    className={primaryBtnClassName}
                    data-psn-analytics={psnAnalytics}
                    testId={
                      testId ? `${testId}-primary-left-button` : undefined
                    }
                  />
                )}
                {leftTertiaryBtnUrl && (
                  <Link
                    variant={LinkVariant.TERTIARY}
                    name={leftTertiaryBtnText ?? ''}
                    url={leftTertiaryBtnUrl}
                    target={leftTertiaryBtnTarget as LinkTarget}
                    analyticsName={leftTertiaryBtnAnalytics}
                    className={tertiaryBtnClassName}
                    data-psn-analytics={psnAnalytics}
                    testId={
                      testId ? `${testId}-tertiary-left-button` : undefined
                    }
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )

  const rightColumn = (
    <div
      className={`TwoColumnBanner-columnRight ${rightBackgroundColor ? getBackgroundConfig(rightBackgroundColor).classes : ''} ${
        rightCertificateRates ? 'u-borderRadius24 u-padding24' : ''
      }`}
    >
      {rightCertificateRates ? (
        renderCertificateRates('right', rightCertificateRates)
      ) : (
        <>
          {renderIconEyebrowImage({
            side: 'right',
            icon: rightIcon,
            iconColor: rightIconColor,
            fill: fillRight,
            stroke: strokeRight,
            eyebrowImage: rightEyebrowImage,
            eyebrowImageAlt: rightEyebrowImageAlt,
            containerClassName: [
              'u-sm-paddingTop4 u-md-paddingTop8 u-paddingVert0',
              !hasBothImages &&
                hasLeftColumnImage &&
                'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40'
            ]
              .filter(Boolean)
              .join(' '),
            testId
          })}
          <div>
            {renderEyebrowText({
              side: 'right',
              eyebrowText: rightEyebrowText,
              className: [
                'Type-5 u-textEyebrow u-paddingVert8 u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40',
                !hasBothImages &&
                  hasLeftColumnImage &&
                  'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40'
              ]
                .filter(Boolean)
                .join(' '),
              testId
            })}
          </div>
          <div>
            {renderInnerContent({
              side: 'right',
              containerClassName: [
                'TwoColumnBanner-innerContent u-paddingBottom8',
                !hasBothImages &&
                  hasLeftColumnImage &&
                  'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40'
              ]
                .filter(Boolean)
                .join(' '),
              columnContent: rightColumnContent,
              hasColumnTable: hasRightColumnTable,
              backgroundColor: rightBackgroundColor,
              textColor,
              descriptionPlaylistId: leftPlaylistId,
              descriptionContainerClassName:
                'TwoColumnBanner-videoDescriptionContainer\n                        u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40',
              playerPlaylistId: rightPlaylistId,
              testId
            })}
          </div>
          <div>
            {(rightPrimaryBtnUrl || rightTertiaryBtnUrl) && (
              <div
                className={[
                  'u-sm-paddingBottom4 u-md-paddingBottom8 u-paddingBottom0 u-paddingTop32',
                  !hasBothImages &&
                    hasLeftColumnImage &&
                    'u-sm-paddingLeft0 u-md-paddingLeft0 u-paddingLeft40',
                  !hasRightColumnImage &&
                    'u-sm-paddingRight0 u-md-paddingRight0 u-paddingRight40'
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {rightPrimaryBtnUrl && (
                  <Link
                    variant={LinkVariant.PRIMARY}
                    name={rightPrimaryBtnText ?? ''}
                    url={rightPrimaryBtnUrl}
                    target={rightPrimaryBtnTarget as LinkTarget}
                    analyticsName={rightPrimaryBtnAnalytics}
                    className={primaryBtnClassName}
                    data-psn-analytics={psnAnalytics}
                    testId={
                      testId ? `${testId}-primary-right-button` : undefined
                    }
                  />
                )}
                {rightTertiaryBtnUrl && (
                  <Link
                    variant={LinkVariant.TERTIARY}
                    name={rightTertiaryBtnText ?? ''}
                    url={rightTertiaryBtnUrl}
                    target={rightTertiaryBtnTarget as LinkTarget}
                    analyticsName={rightTertiaryBtnAnalytics}
                    className={tertiaryBtnClassName}
                    data-psn-analytics={psnAnalytics}
                    testId={
                      testId ? `${testId}-tertiary-right-button` : undefined
                    }
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )

  return (
    <TwoColumnBannerShell props={props}>
      <DisplayGrid columns={2} depth={1}>
        {leftColumn}
        {rightColumn}
      </DisplayGrid>
    </TwoColumnBannerShell>
  )
}
